import { supabase } from '../lib/supabase';

// Remove Gemini import - now using AIMLAPI
// import genAI from '../lib/gemini';

// AIMLAPI configuration
const AIMLAPI_KEY = import.meta.env?.VITE_AIMLAPI_KEY;
const AIMLAPI_BASE_URL = 'https://api.aimlapi.com/v1';

// Validate API key is not a placeholder
const isValidAPIKey = (key) => {
  if (!key) return false;
  const placeholders = ['your-aimlapi-key-here', 'your_api_key', 'placeholder', 'xxx', 'your-'];
  return !placeholders?.some(p => key?.toLowerCase()?.includes(p?.toLowerCase()));
};

/**
 * Service for managing playbook scripts with database-first AI generation
 */
export const scriptService = {
  /**
   * Database-first script generation with AI fallback
   * @param {Object} params - Script generation parameters
   * @returns {Promise<Object>} Generated script with structured data
   */
  async generateScript({ scriptType, industry, prospectName, stage, background, interactions, outcome, painPoints }) {
    try {
      // Get user's industry from profile
      const { data: { user } } = await supabase?.auth?.getUser();
      let userIndustry = industry;
      if (user) {
        const { data: profile } = await supabase?.from('user_profiles')?.select('industry')?.eq('id', user?.id)?.single();
        userIndustry = profile?.industry || industry;
      }

      // STEP 1: Try to load complete template from script_library
      const template = await this.findMatchingTemplate({
        industry: userIndustry,
        scriptType,
        goal: stage?.includes('custom') ? null : stage
      });

      // If template found, use it with placeholder replacement
      if (template && !stage?.includes('custom')) {
        console.log('✅ STEP 1: Using complete template from script_library:', template?.title);
        
        const variables = {
          name: prospectName,
          agent_name: prospectName,
          company: 'our company',
          prospect_name: prospectName
        };

        const personalizedScript = this.replacePlaceholders(template?.templateText, variables);

        return {
          scriptType: template?.scriptType,
          industry: template?.industry,
          subject: template?.title,
          content: personalizedScript,
          toneNotes: template?.coachingNotes ? [template?.coachingNotes] : [],
          situation: `Template: ${template?.goal}`,
          fromTemplate: true
        };
      }

      // STEP 2: Build script from script_blocks
      console.log('⚙️ STEP 2: Attempting to build script from blocks');
      const assembledScript = await this.assembleScriptFromBlocks({
        industry: userIndustry,
        scriptType,
        goal: stage?.includes('custom') ? 'general' : stage
      });

      if (assembledScript) {
        console.log('✅ STEP 2: Successfully assembled script from blocks');
        
        // Personalize the assembled script
        const variables = {
          name: prospectName,
          agent_name: prospectName,
          company: 'our company',
          prospect_name: prospectName
        };

        const personalizedScript = this.replacePlaceholders(assembledScript?.content, variables);

        return {
          scriptType,
          industry: userIndustry,
          subject: assembledScript?.subject || `${scriptType} - ${stage}`,
          content: personalizedScript,
          toneNotes: assembledScript?.toneNotes || [],
          situation: `Assembled from blocks: ${stage}`,
          fromTemplate: true
        };
      }

      // STEP 3: No template or blocks found - use AI generation
      console.log('🤖 STEP 3: No template/blocks found - using AI generation');
      
      const aiScript = await this.generateWithAIMLAPI({
        scriptType,
        industry: userIndustry,
        prospectName,
        stage,
        background,
        interactions,
        outcome,
        painPoints
      });

      // STEP 3b: Immediately cache AI-generated script to script_knowledge_cache
      console.log('💾 STEP 3b: Caching AI response to script_knowledge_cache');
      const scenarioText = `${prospectName} - ${stage} - ${outcome}${background ? ` - ${background}` : ''}`;
      await this.cacheAIScript({
        scriptType,
        industry: userIndustry,
        scenarioText,
        generatedScript: aiScript?.content,
        toneNotes: aiScript?.toneNotes,
        aiModel: 'gpt-4o-mini', // Track which AI model was used
        tags: ['auto_ingested'] // Auto-ingestion tag
      });

      // STEP 4: Save AI-generated script to script_library and script_blocks
      console.log('💾 STEP 4: Saving AI-generated script to script_library and script_blocks');
      await this.saveAIScriptToDatabase({
        scriptType,
        industry: userIndustry,
        goal: stage?.includes('custom') ? 'general' : stage,
        generatedScript: aiScript?.content,
        subject: aiScript?.subject,
        toneNotes: aiScript?.toneNotes
      });

      return aiScript;
    } catch (error) {
      console.error('Error in database-first script generation:', error);
      throw error;
    }
  },

  /**
   * STEP 2: Assemble script from script_blocks
   * @param {Object} criteria - Assembly criteria
   * @returns {Promise<Object|null>} Assembled script or null
   */
  async assembleScriptFromBlocks({ industry, scriptType, goal }) {
    try {
      // Build query for matching blocks
      let query = supabase?.from('script_blocks')?.select('*')?.eq('script_type', scriptType)?.or(`industry.eq.${industry},industry.eq.global`);

      if (goal) {
        query = query?.eq('goal', goal);
      }

      const { data: allBlocks, error } = await query;

      if (error) throw error;

      if (!allBlocks || allBlocks?.length === 0) {
        console.log('⚠️ No script blocks found for criteria');
        return null;
      }

      // Organize blocks by type
      const blocksByType = {
        opening: allBlocks?.filter(b => b?.block_type === 'opening'),
        context: allBlocks?.filter(b => b?.block_type === 'context'),
        question: allBlocks?.filter(b => b?.block_type === 'question'),
        value_frame: allBlocks?.filter(b => b?.block_type === 'value_frame'),
        cta: allBlocks?.filter(b => b?.block_type === 'cta'),
        signoff: allBlocks?.filter(b => b?.block_type === 'signoff')
      };

      // Select one block of each type (prioritize by use_count and created_at)
      const selectBestBlock = (blocks) => {
        if (!blocks || blocks?.length === 0) return null;
        return blocks?.sort((a, b) => {
          if (b?.use_count !== a?.use_count) {
            return b?.use_count - a?.use_count;
          }
          return new Date(b?.created_at) - new Date(a?.created_at);
        })?.[0];
      };

      const selectedBlocks = {
        opening: selectBestBlock(blocksByType?.opening),
        context: selectBestBlock(blocksByType?.context),
        question: selectBestBlock(blocksByType?.question),
        value_frame: selectBestBlock(blocksByType?.value_frame),
        cta: selectBestBlock(blocksByType?.cta),
        signoff: selectBestBlock(blocksByType?.signoff)
      };

      // Check if we have at least the required blocks
      const requiredBlocks = ['opening', 'cta'];
      const hasRequiredBlocks = requiredBlocks?.every(type => selectedBlocks?.[type]);

      if (!hasRequiredBlocks) {
        console.log('⚠️ Missing required blocks (opening, cta)');
        return null;
      }

      // Assemble script in correct order
      const scriptParts = [];
      const toneNotes = [];

      // Order: opening → context → question → value_frame → cta → signoff
      const assemblyOrder = ['opening', 'context', 'question', 'value_frame', 'cta', 'signoff'];
      
      assemblyOrder?.forEach(blockType => {
        const block = selectedBlocks?.[blockType];
        if (block) {
          scriptParts?.push(block?.text);
          if (block?.tone) {
            toneNotes?.push(`${blockType}: ${block?.tone}`);
          }
          
          // Increment use_count for selected blocks
          this.incrementBlockUseCount(block?.id);
        }
      });

      if (scriptParts?.length === 0) {
        return null;
      }

      // For email, first part is subject
      let subject = '';
      let content = '';

      if (scriptType === 'email' && scriptParts?.length > 0) {
        subject = scriptParts?.[0]; // Opening becomes subject for email
        content = scriptParts?.slice(1)?.join('\n\n');
      } else {
        content = scriptParts?.join('\n\n');
      }

      return {
        subject,
        content,
        toneNotes: toneNotes?.filter((v, i, a) => a?.indexOf(v) === i) // Remove duplicates
      };
    } catch (error) {
      console.error('Error assembling script from blocks:', error);
      return null;
    }
  },

  /**
   * Increment use_count for a script block
   * @param {string} blockId - Block ID to update
   */
  async incrementBlockUseCount(blockId) {
    try {
      const { error } = await supabase?.from('script_blocks')?.update({
          use_count: supabase?.raw('use_count + 1')
        })?.eq('id', blockId);

      if (error) console.error('Error incrementing block use_count:', error);
    } catch (error) {
      console.error('Error incrementing block use_count:', error);
    }
  },

  /**
   * STEP 4: Save AI-generated script to script_library and script_blocks
   * @param {Object} scriptData - Script data to save
   */
  async saveAIScriptToDatabase({ scriptType, industry, goal, generatedScript, subject, toneNotes }) {
    try {
      // 4a. Insert full script into script_library
      const libraryTitle = subject || `AI Generated ${scriptType} - ${goal}`;
      
      const { data: savedTemplate, error: libraryError } = await supabase?.from('script_library')?.insert({
          industry,
          script_type: scriptType,
          goal,
          title: libraryTitle,
          template_text: generatedScript,
          coaching_notes: toneNotes?.join(', ') || null
        })?.select()?.single();

      if (libraryError) {
        console.error('Error saving to script_library:', libraryError);
      } else {
        console.log('✅ Saved complete script to script_library:', savedTemplate?.id);
      }

      // 4b. Split script into logical blocks and save to script_blocks with difficulty=1, use_count=0
      const blocks = this.splitScriptIntoBlocks(generatedScript, scriptType);
      
      if (blocks?.length > 0) {
        const blockInserts = blocks?.map(block => ({
          industry,
          script_type: scriptType,
          goal,
          block_type: block?.type,
          text: block?.text,
          tone: block?.tone || null,
          difficulty: 1, // Always set to 1 for auto-ingested blocks
          use_count: 0,  // Start at 0, will increase as blocks get used
          tags: ['auto_ingested'] // Mark as auto-ingested
        }));

        const { error: blocksError } = await supabase?.from('script_blocks')?.insert(blockInserts);

        if (blocksError) {
          console.error('Error saving script blocks:', blocksError);
        } else {
          console.log(`✅ Saved ${blocks?.length} auto-ingested blocks to script_blocks with difficulty=1, use_count=0`);
        }
      }
    } catch (error) {
      console.error('Error saving AI script to database:', error);
      // Don't throw - saving to database shouldn't block script generation
    }
  },

  /**
   * Split script text into logical blocks with inferred types
   * @param {string} scriptText - Full script text
   * @param {string} scriptType - Type of script (sms, email, call_script)
   * @returns {Array} Array of block objects
   */
  splitScriptIntoBlocks(scriptText, scriptType) {
    const blocks = [];
    
    // Split by double newlines or common separators
    const paragraphs = scriptText?.split(/\n\n+/)?.filter(p => p?.trim()?.length > 0);

    if (paragraphs?.length === 0) return blocks;

    // Infer block types based on position and content
    paragraphs?.forEach((text, index) => {
      let blockType = 'context'; // Default
      let tone = null;

      const lowerText = text?.toLowerCase();

      // First paragraph is usually opening
      if (index === 0) {
        blockType = 'opening';
        tone = lowerText?.includes('?') ? 'curious' : 'friendly';
      }
      // Last paragraph is usually CTA or signoff
      else if (index === paragraphs?.length - 1) {
        if (lowerText?.includes('call') || lowerText?.includes('schedule') || lowerText?.includes('book') || 
            lowerText?.includes('demo') || lowerText?.includes('meeting') || lowerText?.includes('appointment')) {
          blockType = 'cta';
          tone = 'direct';
        } else {
          blockType = 'signoff';
          tone = 'professional';
        }
      }
      // Questions are easy to identify
      else if (text?.includes('?')) {
        blockType = 'question';
        tone = 'curious';
      }
      // Value propositions often contain keywords
      else if (lowerText?.includes('help') || lowerText?.includes('save') || lowerText?.includes('increase') ||
               lowerText?.includes('improve') || lowerText?.includes('benefit') || lowerText?.includes('solution')) {
        blockType = 'value_frame';
        tone = 'confident';
      }

      blocks?.push({
        type: blockType,
        text: text?.trim(),
        tone
      });
    });

    return blocks;
  },

  /**
   * Find matching template from script_library
   * @param {Object} criteria - Search criteria
   * @returns {Promise<Object|null>} Matching template or null
   */
  async findMatchingTemplate({ industry, scriptType, goal }) {
    try {
      let query = supabase?.from('script_library')?.select('*')?.eq('script_type', scriptType);

      // Match industry or global templates
      if (industry) {
        query = query?.or(`industry.eq.${industry},industry.eq.global`);
      }

      // Match specific goal if provided
      if (goal) {
        query = query?.eq('goal', goal);
      }

      query = query?.order('created_at', { ascending: false })?.limit(1);

      const { data, error } = await query;

      if (error) throw error;

      if (data && data?.length > 0) {
        return {
          id: data?.[0]?.id,
          industry: data?.[0]?.industry,
          scriptType: data?.[0]?.script_type,
          goal: data?.[0]?.goal,
          title: data?.[0]?.title,
          templateText: data?.[0]?.template_text,
          coachingNotes: data?.[0]?.coaching_notes
        };
      }

      return null;
    } catch (error) {
      console.error('Error finding template:', error);
      return null;
    }
  },

  /**
   * Replace placeholders in template text
   * @param {string} template - Template text with placeholders
   * @param {Object} variables - Variables to replace
   * @returns {string} Personalized text
   */
  replacePlaceholders(template, variables) {
    let result = template;
    
    Object.keys(variables)?.forEach(key => {
      const placeholder = `{${key}}`;
      const regex = new RegExp(placeholder.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g');
      result = result?.replace(regex, variables?.[key] || placeholder);
    });
    
    return result;
  },

  /**
   * Generate script using AIMLAPI (OpenAI-compatible API)
   * @param {Object} params - Script generation parameters
   * @returns {Promise<Object>} Generated script
   */
  async generateWithAIMLAPI({ scriptType, industry, prospectName, stage, background, interactions, outcome, painPoints }) {
    try {
      // Enhanced AIMLAPI key validation with detailed error message
      if (!AIMLAPI_KEY || !isValidAPIKey(AIMLAPI_KEY)) {
        throw new Error(
          '🚨 AIMLAPI Key Not Configured\n\n' + '📋 Steps to fix:\n'+ '1. Visit https://aimlapi.com and sign up for an account\n' +
          '2. Navigate to API Keys section in your dashboard\n'+ '3. Generate a new API key\n'+ '4. Open your project\'s .env file\n' + '5. Replace "your-aimlapi-key-here" with your actual API key\n'+ '6. Save the file and restart your development server\n\n'+ '💡 Example: VITE_AIMLAPI_KEY=sk-proj-abc123xyz...\n\n'+ '⚠️ Important: Make sure to keep your API key private and never commit it to version control!'
        );
      }

      // Build context-aware prompt
      const contextDetails = [];
      if (background) contextDetails?.push(`Background: ${background}`);
      if (interactions) contextDetails?.push(`Previous interactions: ${interactions}`);
      if (painPoints) contextDetails?.push(`Pain points: ${painPoints}`);
      
      const userInput = `
Prospect: ${prospectName}
Industry: ${industry}
Sales Stage: ${stage}
Desired Outcome: ${outcome}
${contextDetails?.join('\n')}
      `?.trim();

      const systemPrompt = `You are Lumo, an AI sales writing assistant inside a coaching app called Earn My Edge.

Your job is to generate clear, concise, high-converting sales messages, not repeat what the user typed.

Context:
- Industry: ${industry}
- Script type: ${scriptType}

Rules:
- Do NOT copy-paste the user text back. Rewrite it in a professional, persuasive way.
- Keep the style simple, confident, and conversational.

For SMS:
- Keep it under 320 characters
- 1 clear call-to-action

For Email:
- Short subject line (max 6-8 words)
- Opening that references their situation
- 1-2 value points
- 1 clear call-to-action

For Call Script:
- Give a short opener
- 2-3 key questions
- 1 suggested way to handle a likely objection
- A closing line aiming for a next step (appointment, quote, etc.)

Return a JSON object with this structure:
{
  "subject": "subject line for email (empty for sms/call)",
  "body": "the full message or script content",
  "tone_notes": ["note 1", "note 2"]
}`;

      // Call AIMLAPI with OpenAI-compatible format
      const response = await fetch(`${AIMLAPI_BASE_URL}/chat/completions`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AIMLAPI_KEY}`
        },
        body: JSON.stringify({
          model: 'gpt-4o-mini', // FIXED: Changed from 'gpt-4o' to more accessible model
          messages: [
            { role: 'system', content: systemPrompt },
            { role: 'user', content: userInput }
          ],
          temperature: 0.7,
          max_tokens: 1000
        })
      });

      if (!response?.ok) {
        const errorText = await response?.text();
        console.error('AIMLAPI error response:', {
          status: response?.status,
          statusText: response?.statusText,
          error: errorText
        });
        
        // Enhanced error messages based on status code
        let userMessage = 'Failed to generate script with AI.';
        if (response?.status === 401) {
          userMessage = 
            '🚨 Authentication Failed\n\n' + 'Your AIMLAPI key appears to be invalid or expired.\n\n'+ '📋 Please verify:\n'+ '1. Your API key in .env is correct\n'+ '2. The key has not been revoked\n'+ '3. Your account is active at https://aimlapi.com\n\n' +
            'If the problem persists, generate a new API key and update your .env file.';
        } else if (response?.status === 429) {
          userMessage = 
            '⏱️ Rate Limit Exceeded\n\n' + 'You\'ve made too many requests in a short period.\n\n' + 'Please wait a moment and try again, or check your usage limits at https://aimlapi.com/dashboard';
        } else if (response?.status >= 500) {
          userMessage = 
            '🔧 Service Temporarily Unavailable\n\n' + 'The AI service is experiencing issues. This is not a problem with your configuration.\n\n'+ 'Please try again in a few moments.';
        } else if (response?.status === 403) {
          // Parse error response to check for quota exhaustion
          let parsedError;
          try {
            parsedError = JSON.parse(errorText);
          } catch (e) {
            parsedError = null;
          }

          // Check if this is a quota exhaustion error
          if (parsedError?.message?.includes('api_credit') || parsedError?.message?.includes('quota')) {
            userMessage = 
              '💳 API Credit Quota Exhausted\n\n' + 'Your AIMLAPI account has run out of API credits.\n\n'+ '📋 To continue using Script Lab:\n'+ '1. Visit https://aimlapi.com/app/billing\n' +
              '2. Update your payment method or add credits\n'+ '3. Choose a plan that fits your usage needs\n\n'+ '💡 Tip: Script Lab uses database templates first to minimize AI calls.\n'+ '   Consider using pre-built templates from the Template Library when available.';
          } else {
            userMessage = 
              '🚫 Access Forbidden\n\n' + 'Your API key may not have access to the requested model (gpt-4o-mini).\n\n'+ 'Please check:\n'+ '1. Your subscription plan at https://aimlapi.com/pricing\n' +
              '2. Model access permissions for your API key\n'+ '3. Account status at https://aimlapi.com/app/billing';
          }
        } else {
          userMessage = `⚠️ AI API Error (Status ${response?.status})\n\n${errorText?.substring(0, 200)}`;
        }
        
        throw new Error(userMessage);
      }

      let result = await response?.json();
      const generatedText = result?.choices?.[0]?.message?.content;

      // Parse JSON response
      let generatedContent;
      try {
        // Remove markdown code blocks if present
        const jsonText = generatedText?.replace(/```json\n?/g, '')?.replace(/```\n?/g, '')?.trim();
        generatedContent = JSON.parse(jsonText);
      } catch (parseError) {
        console.error('JSON parsing error, using fallback:', parseError);
        // Fallback if JSON parsing fails
        generatedContent = {
          subject: '',
          body: generatedText,
          tone_notes: ['Professional', 'Conversational']
        };
      }

      return {
        scriptType,
        industry,
        subject: generatedContent?.subject || '',
        content: generatedContent?.body,
        toneNotes: generatedContent?.tone_notes || [],
        situation: `${industry} - ${outcome}`,
        fromTemplate: false,
        aiModel: 'gpt-4o-mini' // Track AI model used
      };
    } catch (error) {
      console.error('Error generating script with AIMLAPI:', error);
      
      // Re-throw with user-friendly message if it's already formatted
      if (error?.message?.includes('🚨') || error?.message?.includes('⚠️') || error?.message?.includes('⏱️')) {
        throw error;
      }
      
      // Check for network errors
      if (error?.message?.includes('fetch') || error?.message?.includes('network')) {
        throw new Error(
          '🌐 Network Connection Error\n\n' + 'Unable to reach the AI service. Please check:\n'+ '1. Your internet connection\n'+ '2. Firewall settings\n'+ '3. VPN configuration if applicable'
        );
      }
      
      // Wrap generic errors with helpful context
      throw new Error(
        `⚠️ Script Generation Failed\n\n` +
        `Error: ${error?.message}\n\n` +
        `Please check:\n` +
        `1. Your AIMLAPI key configuration in .env\n` +
        `2. Your internet connection\n` +
        `3. Your AIMLAPI account status`
      );
    }
  },

  /**
   * Cache AI-generated script in script_knowledge_cache with AI model and tags
   * @param {Object} scriptData - Script data to cache
   * @returns {Promise<Object>} Cached script with ID
   */
  async cacheAIScript({ scriptType, industry, scenarioText, generatedScript, toneNotes, aiModel, tags }) {
    try {
      if (!generatedScript || generatedScript?.trim() === '') {
        console.log('⚠️ Skipping cache - no AI-generated content');
        return null;
      }

      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) return null;

      const { data, error } = await supabase?.from('script_knowledge_cache')?.insert({
          user_id: user?.id,
          script_type: scriptType,
          industry: industry,
          scenario_text: scenarioText,
          generated_script: generatedScript,
          tone_notes: Array?.isArray(toneNotes) ? toneNotes?.join(', ') : toneNotes,
          ai_model: aiModel || 'gpt-4o-mini',
          tags: tags || ['auto_ingested'],
          quality_flag: 'pending_review',
          rating: null,
          promoted: false
        })?.select()?.single();

      if (error) {
        console.error('Error caching AI script:', error);
        return null;
      } else {
        console.log('✅ AI-generated script cached with model:', aiModel, 'and tags:', tags);
        return {
          id: data?.id,
          scriptType: data?.script_type,
          industry: data?.industry,
          scenarioText: data?.scenario_text,
          generatedScript: data?.generated_script,
          toneNotes: data?.tone_notes,
          aiModel: data?.ai_model,
          tags: data?.tags,
          rating: data?.rating,
          promoted: data?.promoted
        };
      }
    } catch (error) {
      console.error('Error caching AI script:', error);
      return null;
    }
  },

  /**
   * Rate a cached script and auto-promote if rating >= 4
   * @param {string} cacheId - script_knowledge_cache ID
   * @param {number} rating - Rating value (1 or 5)
   * @returns {Promise<Object>} Update result
   */
  async rateScript(cacheId, rating) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Validate rating
      if (![1, 5]?.includes(rating)) {
        throw new Error('Rating must be 1 or 5');
      }

      // Update rating
      const { data: script, error: updateError } = await supabase
        ?.from('script_knowledge_cache')
        ?.update({ rating })
        ?.eq('id', cacheId)
        ?.eq('user_id', user?.id)
        ?.select()
        ?.single();

      if (updateError) throw updateError;

      // If thumbs up (rating 5) and not promoted yet, auto-promote
      if (rating === 5 && !script?.promoted) {
        await this.promoteScriptToLibrary(cacheId);
      }

      return {
        success: true,
        rating,
        promoted: rating === 5 && !script?.promoted
      };
    } catch (error) {
      console.error('Error rating script:', error);
      throw error;
    }
  },

  /**
   * Promote script to script_library and split into script_blocks
   * @param {string} cacheId - script_knowledge_cache ID
   * @returns {Promise<void>}
   */
  async promoteScriptToLibrary(cacheId) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      // Get the cached script
      const { data: cachedScript, error: fetchError } = await supabase
        ?.from('script_knowledge_cache')
        ?.select('*')
        ?.eq('id', cacheId)
        ?.eq('user_id', user?.id)
        ?.single();

      if (fetchError || !cachedScript) {
        throw new Error('Script not found or access denied');
      }

      // Check if already promoted
      if (cachedScript?.promoted) {
        console.log('⚠️ Script already promoted');
        return;
      }

      // Extract goal from scenario_text (simple heuristic)
      const scenarioLower = cachedScript?.scenario_text?.toLowerCase() || '';
      let goal = 'general';
      
      if (scenarioLower?.includes('first') || scenarioLower?.includes('intro')) goal = 'first_touch';
      else if (scenarioLower?.includes('follow')) goal = 'follow_up';
      else if (scenarioLower?.includes('close') || scenarioLower?.includes('closing')) goal = 'closing';
      else if (scenarioLower?.includes('objection')) goal = 'objection_handling';
      else if (scenarioLower?.includes('demo')) goal = 'demo_request';

      // 1. Insert into script_library
      const { data: savedTemplate, error: libraryError } = await supabase
        ?.from('script_library')
        ?.insert({
          industry: cachedScript?.industry,
          script_type: cachedScript?.script_type,
          goal,
          title: `High-Rated Script - ${cachedScript?.script_type}`,
          template_text: cachedScript?.generated_script,
          coaching_notes: cachedScript?.tone_notes
        })
        ?.select()
        ?.single();

      if (libraryError) {
        console.error('Error saving to script_library:', libraryError);
        throw libraryError;
      }

      console.log('✅ Promoted script to script_library:', savedTemplate?.id);

      // 2. Split and save to script_blocks with difficulty=1, use_count=0
      const blocks = this.splitScriptIntoBlocks(
        cachedScript?.generated_script, 
        cachedScript?.script_type
      );

      if (blocks?.length > 0) {
        const blockInserts = blocks?.map(block => ({
          industry: cachedScript?.industry,
          script_type: cachedScript?.script_type,
          goal,
          block_type: block?.type,
          text: block?.text,
          tone: block?.tone || null,
          difficulty: 1, // Always set to 1 for promoted blocks
          use_count: 0,  // Reset to 0 when promoted
          tags: ['promoted', 'auto_ingested'] // Add promoted tag
        }));

        const { error: blocksError } = await supabase
          ?.from('script_blocks')
          ?.insert(blockInserts);

        if (blocksError) {
          console.error('Error saving script blocks:', blocksError);
        } else {
          console.log(`✅ Saved ${blocks?.length} promoted blocks to script_blocks with difficulty=1, use_count=0`);
        }
      }

      // 3. Mark as promoted
      const { error: promotedError } = await supabase
        ?.from('script_knowledge_cache')
        ?.update({ promoted: true })
        ?.eq('id', cacheId);

      if (promotedError) {
        console.error('Error marking as promoted:', promotedError);
      } else {
        console.log('✅ Marked script as promoted in cache');
      }
    } catch (error) {
      console.error('Error promoting script to library:', error);
      throw error;
    }
  },

  /**
   * Save a generated script to the database
   * @param {Object} script - Script data to save
   * @returns {Promise<Object>} Saved script with database ID
   */
  async saveScript({ scriptType, title, industry, situation, subject, content, toneNotes }) {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('playbook_scripts')?.insert({
          user_id: user?.id,
          script_type: scriptType,
          title,
          industry,
          situation,
          subject: subject || null,
          content,
          tone_notes: toneNotes,
          usage_count: 0
        })?.select()?.single();

      if (error) throw error;

      // Convert to camelCase for React
      return {
        id: data?.id,
        userId: data?.user_id,
        scriptType: data?.script_type,
        title: data?.title,
        industry: data?.industry,
        situation: data?.situation,
        subject: data?.subject,
        content: data?.content,
        toneNotes: data?.tone_notes,
        usageCount: data?.usage_count,
        createdAt: data?.created_at
      };
    } catch (error) {
      console.error('Error saving script:', error);
      throw error;
    }
  },

  /**
   * Get all scripts for the current user
   * @returns {Promise<Array>} List of user's scripts
   */
  async getAll() {
    try {
      const { data: { user } } = await supabase?.auth?.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase?.from('playbook_scripts')?.select('*')?.eq('user_id', user?.id)?.order('created_at', { ascending: false });

      if (error) throw error;

      // Convert to camelCase
      return data?.map(script => ({
        id: script?.id,
        userId: script?.user_id,
        scriptType: script?.script_type,
        title: script?.title,
        industry: script?.industry,
        situation: script?.situation,
        subject: script?.subject,
        content: script?.content,
        toneNotes: script?.tone_notes,
        usageCount: script?.usage_count,
        createdAt: script?.created_at,
        updatedAt: script?.updated_at
      }));
    } catch (error) {
      console.error('Error fetching scripts:', error);
      throw error;
    }
  },

  /**
   * Update script usage count
   * @param {string} scriptId - Script ID to update
   * @returns {Promise<void>}
   */
  async incrementUsageCount(scriptId) {
    try {
      const { error } = await supabase?.rpc('increment_usage_count', {
        script_id: scriptId
      });

      if (error) throw error;
    } catch (error) {
      console.error('Error incrementing usage count:', error);
    }
  },

  /**
   * Delete a script
   * @param {string} scriptId - Script ID to delete
   * @returns {Promise<void>}
   */
  async delete(scriptId) {
    try {
      const { error } = await supabase?.from('playbook_scripts')?.delete()?.eq('id', scriptId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting script:', error);
      throw error;
    }
  }
};

// ✅ Script Library Service Methods (for global templates)
export const scriptLibraryService = {
  /**
   * Get all script library templates (optionally filtered by industry/type)
   */
  async getAll(filters = {}) {
    let query = supabase?.from('script_library')?.select('*')?.order('created_at', { ascending: false });

    // Apply filters if provided
    if (filters?.industry) {
      query = query?.eq('industry', filters?.industry);
    }
    if (filters?.scriptType) {
      query = query?.eq('script_type', filters?.scriptType);
    }
    if (filters?.goal) {
      query = query?.eq('goal', filters?.goal);
    }

    const { data, error } = await query;
    
    if (error) throw error;

    // Convert snake_case to camelCase
    return data?.map(template => ({
      id: template?.id,
      industry: template?.industry,
      scriptType: template?.script_type,
      goal: template?.goal,
      title: template?.title,
      templateText: template?.template_text,
      coachingNotes: template?.coaching_notes,
      createdAt: template?.created_at,
      updatedAt: template?.updated_at
    }));
  },

  /**
   * Get templates by industry (including 'global' templates)
   */
  async getByIndustry(industry) {
    const { data, error } = await supabase?.from('script_library')?.select('*')?.or(`industry.eq.${industry},industry.eq.global`)?.order('created_at', { ascending: false });
    
    if (error) throw error;

    return data?.map(template => ({
      id: template?.id,
      industry: template?.industry,
      scriptType: template?.script_type,
      goal: template?.goal,
      title: template?.title,
      templateText: template?.template_text,
      coachingNotes: template?.coaching_notes,
      createdAt: template?.created_at,
      updatedAt: template?.updated_at
    }));
  },

  /**
   * Get single template by ID
   */
  async getById(id) {
    const { data, error } = await supabase?.from('script_library')?.select('*')?.eq('id', id)?.single();
    
    if (error) throw error;

    return {
      id: data?.id,
      industry: data?.industry,
      scriptType: data?.script_type,
      goal: data?.goal,
      title: data?.title,
      templateText: data?.template_text,
      coachingNotes: data?.coaching_notes,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Use template to generate personalized script
   * Replaces placeholders like {name}, {agent_name} with actual values
   */
  useTemplate(template, variables = {}) {
    let personalizedText = template?.templateText;

    // Replace all placeholders
    Object.keys(variables)?.forEach(key => {
      const placeholder = `{${key}}`;
      personalizedText = personalizedText?.replace(new RegExp(placeholder, 'g'), variables?.[key]);
    });

    return {
      scriptType: template?.scriptType,
      title: template?.title,
      content: personalizedText,
      coachingNotes: template?.coachingNotes,
      industry: template?.industry,
      goal: template?.goal
    };
  },

  /**
   * Admin only: Create new template
   */
  async create(template) {
    const { data, error } = await supabase?.from('script_library')?.insert({
        industry: template?.industry,
        script_type: template?.scriptType,
        goal: template?.goal,
        title: template?.title,
        template_text: template?.templateText,
        coaching_notes: template?.coachingNotes
      })?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      industry: data?.industry,
      scriptType: data?.script_type,
      goal: data?.goal,
      title: data?.title,
      templateText: data?.template_text,
      coachingNotes: data?.coaching_notes,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Admin only: Update existing template
   */
  async update(id, updates) {
    const updateData = {};
    if (updates?.industry) updateData.industry = updates?.industry;
    if (updates?.scriptType) updateData.script_type = updates?.scriptType;
    if (updates?.goal) updateData.goal = updates?.goal;
    if (updates?.title) updateData.title = updates?.title;
    if (updates?.templateText) updateData.template_text = updates?.templateText;
    if (updates?.coachingNotes !== undefined) updateData.coaching_notes = updates?.coachingNotes;

    const { data, error } = await supabase?.from('script_library')?.update(updateData)?.eq('id', id)?.select()?.single();

    if (error) throw error;

    return {
      id: data?.id,
      industry: data?.industry,
      scriptType: data?.script_type,
      goal: data?.goal,
      title: data?.title,
      templateText: data?.template_text,
      coachingNotes: data?.coaching_notes,
      createdAt: data?.created_at,
      updatedAt: data?.updated_at
    };
  },

  /**
   * Admin only: Delete template
   */
  async delete(id) {
    const { error } = await supabase?.from('script_library')?.delete()?.eq('id', id);

    if (error) throw error;
  }
};